import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { getErrorMessageFromCode, CarrotErrorCodes } from "#/utils/CarrotErrors"
import { useLocation, useHistory } from "react-router-dom"
import queryString from "query-string"
import { useDispatch } from "react-redux"
import { getDeepLinkReturnUrl, redirectToSamlExternalLogin } from "#/utils/RedirectToSamlExternalLogin"
import { persistEmail, signIn } from "#/redux/actions/loginActions"
import { useQueryClient } from "@tanstack/react-query"
import {
  attemptBiometricsCredentialsRetrieval,
  shouldAttemptBioAuthenticationLogin,
  showBiometricsEnrollment
} from "carrot-api/MobileNativeClient"
import {
  trackBiometricAuth,
  trackBiometricAuthAttempt,
  trackBiometricAuthEnrollment,
  useBiometricAuthEnrollment
} from "../../context/BiometricAuthEnrollment/BiometricAuthEnrollmentContext"
import { useAuthentication } from "../../context/authentication/AuthenticationProvider"
import { SignIn } from "#/pages/sign-in"
import { defineMessage, MessageDescriptor, useIntl } from "react-intl"
import { Link } from "@carrotfertility/carotene-core"
import { Form } from "@carrotfertility/carotene-core-x"
import { EmployeeSupportUrl } from "#/utils/EmployeeSupportLink"
import { carrotClient } from "#/utils/CarrotClient"
import { HttpErrors } from "#/utils/HttpErrors"
import { reportError } from "#/utils/ErrorReporting"
import { PreloginOutcome } from "#/lib/carrot-api/types/Prelogin"

type LoginContainerState = {
  username: string | null
  password: string | null
  submitting: boolean
  showPasswordInput: boolean
  showEmailInput: boolean
  enableEmailInput: boolean
  showSignUp: boolean
  showContactUs: boolean
  shake: boolean
  errorHasLink: boolean
  errorMessage: MessageDescriptor | null
  isErrorPage: boolean
  samlConfig: any
  returnUrl: string | null
  isIdpOnlySso: boolean
  showSsoAlert: boolean
  showSubmitButton: boolean
}

const defaultState: LoginContainerState = {
  username: null,
  password: null,
  submitting: false,
  showPasswordInput: false,
  showEmailInput: true,
  samlConfig: null,
  shake: false,
  errorHasLink: false,
  errorMessage: null,
  isErrorPage: false,
  returnUrl: null,
  isIdpOnlySso: false,
  enableEmailInput: true,
  showSignUp: true,
  showContactUs: false,
  showSsoAlert: false,
  showSubmitButton: true
}

export type LoginFormData = {
  username: string
  password: string
  setupBiometricAuth: boolean
}

export const MultiFactorAuthRequired = "MultiFactorAuthRequired"

const LoginContainer = (): React.ReactElement => {
  const location = useLocation()
  const history = useHistory()
  const [state, setState] = useState(defaultState)
  const [enrollInBioAuth, setEnrollInBioAuth] = useState(false)
  const passwordFailedRef = useRef(false)
  const dispatch = useDispatch()
  const passwordInputRef = useRef()
  const queryClient = useQueryClient()
  const {
    setBiometricAuthEnrollmentSuccess,
    setBiometricAuthEnrollmentFailure,
    resetBiometricAuthEnrollmentState,
    disableBiometricAuth
  } = useBiometricAuthEnrollment()
  const { isExplicitLogout, resetExplicitLogout } = useAuthentication()
  const intl = useIntl()

  useState(() => {
    async function execute() {
      if (!isExplicitLogout && (await shouldAttemptBioAuthenticationLogin())) {
        setTimeout(async () => {
          trackBiometricAuthAttempt()
          await attemptBiometricsCredentialsRetrieval(bioAuthLogin)
        }, 1000)
      }
    }
    execute()
  })

  useEffect(() => {
    queryClient.clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const onSubmit = async (formData: LoginFormData) => {
    if (formData.password) {
      await login(formData.username, formData.password, formData.setupBiometricAuth)
    } else {
      await preloginLookup(formData.username)
    }
  }

  useEffect(() => {
    const parsedQueryString = queryString.parse(location.search)
    const errorCode = parsedQueryString?.errorCode
    const isErrorPage = Boolean(errorCode)
    const errorMessage = errorCode ? getErrorMessageFromCode(Array.isArray(errorCode) ? errorCode[0] : errorCode) : null
    const returnUrl = (() => {
      if (!parsedQueryString.ReturnUrl) {
        return null
      }
      return Array.isArray(parsedQueryString.ReturnUrl) ? parsedQueryString.ReturnUrl[0] : parsedQueryString.ReturnUrl
    })()

    setState({
      ...state,
      errorMessage,
      // If we have an error message from the URL upon loading this view, that means we want to display only the message and "Sign in" button, so showEmailInput should be false
      showEmailInput: !isErrorPage,
      isErrorPage,
      returnUrl,
      errorHasLink: Boolean(
        errorCode === CarrotErrorCodes.EMPLOYEE_INACTIVE || CarrotErrorCodes.SAML_UNSUCCESSFUL_OPERATION_EXCEPTION
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  useLayoutEffect(() => {
    if (passwordInputRef.current) {
      return (passwordInputRef.current as HTMLInputElement).focus()
    }
  }, [state.showEmailInput])

  // Listen for back button press
  useEffect(() => {
    const unlisten = history.listen((location, action) => {
      if (action === "POP") {
        setState({
          ...state,
          submitting: false,
          showPasswordInput: false,
          errorMessage: null,
          errorHasLink: false,
          showContactUs: false,
          showSignUp: true,
          enableEmailInput: true,
          showSsoAlert: false,
          showSubmitButton: true
        })
      }
    })
    return () => unlisten()
  }, [history, state])

  const preloginLookup = async (email: string) => {
    if (state.isErrorPage) {
      setState({
        ...state,
        isErrorPage: false,
        showEmailInput: true,
        showPasswordInput: false,
        errorMessage: null,
        errorHasLink: false
      })
      return
    }

    setState({
      ...state,
      submitting: true,
      shake: false,
      errorHasLink: false
    })

    let response
    try {
      response = await carrotClient.prelogin(email)
    } catch (error) {
      setState({
        ...state,
        submitting: false,
        errorMessage: defineMessage({ defaultMessage: "Connection problem" }),
        shake: true,
        errorHasLink: false
      })
      return
    }

    switch (response.outcome) {
      case PreloginOutcome.SsoRedirect:
        redirectToSamlExternalLogin(response.sso.idpEntityId, state.returnUrl ?? getDeepLinkReturnUrl())
        return

      case PreloginOutcome.SsoIdpOnly:
        showIdpOnlySsoScreen()
        return

      case PreloginOutcome.Unregistered:
        dispatch(persistEmail(email))
        history.push(`/signup`)
        return

      case PreloginOutcome.Password:
      default:
        // PASSWORD also covers not-found accounts, so keep the Sign Up link visible (enumeration resistance).
        dispatch(persistEmail(email))
        setState({
          ...state,
          submitting: false,
          showPasswordInput: true,
          enableEmailInput: false,
          samlConfig: response.sso
            ? {
                samlIdpEntityId: response.sso.idpEntityId
              }
            : null,
          errorMessage: null,
          shake: false,
          errorHasLink: false,
          showContactUs: true,
          showSignUp: true,
          showSsoAlert: false,
          isIdpOnlySso: false
        })

        if (passwordInputRef.current) {
          return (passwordInputRef.current as HTMLInputElement).focus()
        }
        return
    }
  }

  const clearErrorCodeFromUrl = () => {
    const params = new URLSearchParams(location.search)
    if (params.has("errorCode")) {
      params.delete("errorCode")
      history.replace({ ...location, search: params.toString() })
    }
  }

  /**
   * Persists the locale the user selected before logging in.
   *
   * Before authentication, the user may choose a display language on the login page.
   * That selection is stored in sessionStorage under the key "unauthPreferredLocale"
   * (written by UnauthPreferredLocale.tsx). Because sessionStorage is not tied to the
   * user's account, we must write it to their profile immediately after they authenticate.
   *
   * This function reads and clears the sessionStorage value, then calls updateUser to save
   * it server-side. Errors are caught and reported without interrupting the login flow so
   * that a failed locale update never blocks the user from signing in.
   *
   * After authentication, LocaleService on the backend determines whether the selected
   * language is available for the user's account.
   */
  const updatePreferredLocale = async () => {
    // eslint-disable-next-line no-restricted-syntax
    const preferredLocale = sessionStorage.getItem("unauthPreferredLocale")
    sessionStorage.removeItem("unauthPreferredLocale")
    // eslint-disable-next-line no-restricted-syntax
    if (preferredLocale) {
      try {
        // eslint-disable-next-line no-restricted-syntax -- We update the user's preferredLocale and Carrot-app's LocaleService will then determine if they can use the selected language once signed in.
        await carrotClient.updateUser({ preferredLocale })
      } catch (error) {
        reportError(error)
      }
    }
  }

  const login = async (email: string, password: string, registerBioAuthentication = false) => {
    setState({
      ...state,
      submitting: true,
      shake: false
    })

    try {
      const response = await carrotClient.login(email, password)

      // The server decides whether MFA is required and signals it in the response; the client just reacts.
      if (response?.message === MultiFactorAuthRequired) {
        history.push(`/login/enter-code?email=${encodeURIComponent(email)}`)
        return
      }
    } catch (error) {
      // A deactivated account is revealed here (403) — only after a correct password — rather than at email entry.
      const isDeactivated = error.name === HttpErrors.FORBIDDEN
      const isWrongPassword = error.name === HttpErrors.BAD_REQUEST

      setState({
        ...state,
        submitting: false,
        errorMessage: isDeactivated
          ? defineMessage({
              defaultMessage:
                "It looks like your account has recently been deactivated. If you need to submit receipts for service prior to leaving your benefit sponsor, please <link>contact us</link>. If you feel that your account has been deactivated in error, please reach out to your internal benefits team for next steps."
            })
          : isWrongPassword
          ? defineMessage({ defaultMessage: "Password does not match." })
          : defineMessage({
              defaultMessage:
                "Oops, something went wrong. Please try again later or <link>contact us</link> for assistance."
            }),
        shake: true,
        errorHasLink: !isWrongPassword
      })

      passwordFailedRef.current = true
      return
    }

    if (registerBioAuthentication) {
      tryBioAuthEnrollment(email, password)
    } else {
      setState({ ...state, submitting: false })
      clearErrorCodeFromUrl()
      await updatePreferredLocale()
      dispatch(signIn())
      resetExplicitLogout()
    }
  }

  const bioAuthEnrollmentSuccess = () => {
    trackBiometricAuthEnrollment(true)
    setBiometricAuthEnrollmentSuccess()
    clearErrorCodeFromUrl()
    setState({ ...state, submitting: false })
  }

  const bioAuthEnrollmentFailure = () => {
    trackBiometricAuthEnrollment(false)
    setBiometricAuthEnrollmentFailure()
    // We've logged in successfully, however, for some reason biometric enrollment failed
    setState({ ...state, submitting: false })
  }

  const tryBioAuthEnrollment = (email: string, password: string) => {
    showBiometricsEnrollment(
      email,
      password,
      () => {
        // Timeout is for visual purposes to slow the enrollment process down a touch. On iOS the app logs in before
        // the face id screen completes, which seems off
        setTimeout(() => {
          bioAuthEnrollmentSuccess()
        }, 1000)
      },
      bioAuthEnrollmentFailure
    )
  }

  const bioAuthLogin = async (email: string, password: string): Promise<void> => {
    await login(email, password, false)
    if (passwordFailedRef.current) {
      setState({
        ...state,
        submitting: false,
        shake: false,
        showPasswordInput: false,
        errorMessage: defineMessage({
          defaultMessage: "Biometric authentication failed, please type your credentials again."
        })
      })
      trackBiometricAuth(false)
      passwordFailedRef.current = false
      // This is for change in password failing bioauth
      disableBiometricAuth()
      setEnrollInBioAuth(true)
    } else {
      trackBiometricAuth(true)
    }
  }

  const formattedErrorMessage: React.ReactNode = (() => {
    if (!state.errorMessage) {
      return null
    }

    if (state.errorHasLink) {
      return intl.formatMessage(state.errorMessage, {
        link: (linkContent) => (
          <Link style={{ color: "inherit" }} href={EmployeeSupportUrl} target="_blank">
            {linkContent}
          </Link>
        ),
        supportEmailLink: (linkContent) => (
          <Link style={{ color: "inherit" }} href="mailto:support@get-carrot.com">
            {linkContent}
          </Link>
        )
      })
    }

    return intl.formatMessage(state.errorMessage)
  })()

  const showIdpOnlySsoScreen = () => {
    setState({
      ...state,
      submitting: false,
      showPasswordInput: false,
      samlConfig: null,
      errorMessage: null,
      shake: false,
      errorHasLink: false,
      isIdpOnlySso: true,
      enableEmailInput: false,
      showContactUs: true,
      showSignUp: false,
      showSsoAlert: true,
      showSubmitButton: false
    })
  }

  const handleSuccessfulBiometricAuth = async () => {
    await updatePreferredLocale()
    dispatch(signIn())
    resetBiometricAuthEnrollmentState()
  }

  return (
    <Form onSubmit={onSubmit}>
      <SignIn
        onSubmit={onSubmit}
        errorMessage={formattedErrorMessage}
        passwordInputRef={passwordInputRef}
        shake={state.shake}
        showEmailInput={state.showEmailInput}
        showPasswordInput={state.showPasswordInput}
        submitting={state.submitting}
        successfulBiometricAuthAction={handleSuccessfulBiometricAuth}
        bioAuthLogin={bioAuthLogin}
        enrollInBioAuth={enrollInBioAuth}
        samlConfig={state.samlConfig}
        redirectToSamlExternalLogin={redirectToSamlExternalLogin}
        returnUrl={state.returnUrl}
        isIdpOnlySso={state.isIdpOnlySso}
        enableEmailInput={state.enableEmailInput}
        showContactUs={state.showContactUs}
        showSignUp={state.showSignUp}
        showSsoAlert={state.showSsoAlert}
        showIdpOnlySsoScreen={showIdpOnlySsoScreen}
        showSubmitButton={state.showSubmitButton}
      />
    </Form>
  )
}

export default LoginContainer
